/**
 * Hook for managing chat context linking state.
 * Derives locked state from chat data and syncs linked context from backend.
 */

import {
  clearLinkedContextAtom,
  linkedContextOriginAtom,
  linkedContextOriginFor,
  linkedPatientContextAtom,
  linkedSessionContextAtom,
  PENDING_LINKED_CONTEXT_ORIGIN,
  type LinkedPatientContext,
  type LinkedSessionContext,
} from '@/atoms/chatAtoms';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useQueryAdminSessions } from '@/hooks/query/useQuerySessionsV2';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useTranslations } from '@heidi/runtime/i18n';
import { GET_PATIENT_PROFILES } from '@heidi/patients/contracts';
import { getPatientProfiles } from '@heidi/patients/data';
import { GET_SESSION_BY_ID, isOfflineLocalSession, type QuerySessionResponse } from '@heidi/session/contracts';
import { fetchSessionByIdQueryFn, getSessionDataConfig } from '@heidi/session/data';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

function buildPatientDisplayName(
  patient:
    | {
        first_name?: string | null;
        last_name?: string | null;
        demographic_string?: string | null;
      }
    | null
    | undefined,
  fallback: string
): string {
  if (!patient) return fallback;
  const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(' ');
  return fullName || patient.demographic_string || fallback;
}

interface UseChatContextLinkingProps {
  /** The current chat ID (null/undefined for new chat) */
  chatId: string | null | undefined;
  /** Whether the chat is currently streaming/loading */
  isLoading?: boolean;
  /** Session ID from chat metadata (passed from useChatBackend) */
  sessionId?: string | null;
  /** Patient profile ID from chat metadata (passed from useChatBackend) */
  patientProfileId?: string | null;
  /** Whether the chat already has messages (mid-chat vs create-time linking). */
  hasMessages?: boolean;
  /** EVI-2279 — mid-chat linking requires backend persist rollout. */
  isMidChatClinicalLinkPersistEnabled?: boolean;
}

interface UseChatContextLinkingReturn {
  /** Whether context is locked (only while streaming/creating) */
  isContextLocked: boolean;
  /** Currently linked session */
  linkedSession: LinkedSessionContext | null;
  /** Currently linked patient */
  linkedPatient: LinkedPatientContext | null;
  /** Set the linked session */
  setLinkedSession: (session: LinkedSessionContext | null) => void;
  /** Set the linked patient */
  setLinkedPatient: (patient: LinkedPatientContext | null) => void;
  /** Clear all linked context */
  clearLinkedContext: () => void;
}

/**
 * Hook that manages chat context linking state.
 *
 * Simple rules:
 * 1. Use frontend atoms as source of truth until backend loads
 * 2. Track which chat the context belongs to via linkedContextOriginAtom
 * 3. Clear when navigating to a different chat
 * 4. Sync from backend when loading an existing chat
 */
export function useChatContextLinking({
  chatId,
  isLoading = false,
  sessionId,
  patientProfileId,
  hasMessages = false,
  isMidChatClinicalLinkPersistEnabled = false,
}: UseChatContextLinkingProps): UseChatContextLinkingReturn {
  const { t } = useTranslations();
  const { isAuthenticated } = useIsAuthenticated();
  const authToken = useAuthToken();
  const sessionDataConfig = getSessionDataConfig();
  const linkedSessionPlaceholder = t('chat.contextLinking.linkedSession');
  const linkedPatientPlaceholder = t('chat.contextLinking.linkedPatient');
  const linkedSession = useAtomValue(linkedSessionContextAtom);
  const linkedPatient = useAtomValue(linkedPatientContextAtom);
  const setLinkedSessionAtom = useSetAtom(linkedSessionContextAtom);
  const setLinkedPatientAtom = useSetAtom(linkedPatientContextAtom);
  const clearContextAtom = useSetAtom(clearLinkedContextAtom);
  const [contextOrigin, setContextOrigin] = useAtom(linkedContextOriginAtom);

  // Track last chatId to detect changes
  const lastChatIdRef = useRef<string | null | undefined>(undefined);

  // Track the last backend values we synced into the atoms. The sync effect
  // below only overwrites the atoms when the *backend* props change (initial
  // load, data refresh) — not when the atoms change. This lets a user link a
  // new patient/session mid-chat (after messages exist) without the stale
  // backend `session_id`/`patient_profile_id` immediately reverting their
  // selection. See EVI-2243.
  const lastSyncedBackendRef = useRef<{
    sessionId: string | null | undefined;
    patientProfileId: string | null | undefined;
  }>({ sessionId: undefined, patientProfileId: undefined });

  // Fetch patient profiles to look up patient names
  const { data: patientProfiles, isFetched: isPatientProfilesFetched } =
    useQueryPatientProfiles();

  // Fetch sessions to look up session names
  const { data: sessionsData } = useQueryAdminSessions();

  // Locked while streaming/creating, or when mid-chat linking is unavailable.
  // Create-time linking (no messages yet) stays open regardless of flag.
  // Mid-chat linking after messages exist requires EVI-2279 persist rollout.
  const isContextLocked = useMemo(() => {
    if (isLoading) return true;
    if (hasMessages && !isMidChatClinicalLinkPersistEnabled) return true;
    return false;
  }, [isLoading, hasMessages, isMidChatClinicalLinkPersistEnabled]);

  // Look up session details from the sessions list
  const sessionDetails = useMemo(() => {
    if (!sessionId || !sessionsData?.pages) return null;
    const allSessions = sessionsData.pages.flatMap((page) => page.sessions);
    return allSessions.find((s) => s.session_id === sessionId) ?? null;
  }, [sessionId, sessionsData?.pages]);

  // Look up patient details from the patient profiles list
  const patientDetailsFromList = useMemo(() => {
    if (!patientProfileId || !patientProfiles) return null;
    return patientProfiles.find((p) => p.id === patientProfileId) ?? null;
  }, [patientProfileId, patientProfiles]);

  // Paginated session/patient lists may not include the linked entity after a
  // reload — fetch by id so badges show real names instead of placeholders.
  const cachedLinkedSession = sessionId
    ? sessionDataConfig
        .getQueryClient()
        .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }])
    : undefined;

  const needsSessionById =
    isAuthenticated &&
    !!authToken &&
    !!chatId &&
    !!sessionId &&
    !sessionDetails &&
    !isOfflineLocalSession(cachedLinkedSession);

  const { data: sessionById } = useQuery({
    queryKey: [GET_SESSION_BY_ID, { sessionId }],
    enabled: needsSessionById,
    queryFn: ({ signal }) => fetchSessionByIdQueryFn({ sessionId: sessionId!, signal }),
    retry: (failureCount, error) =>
      sessionDataConfig.isSessionNotFoundError(error) ? false : failureCount < 1,
    networkMode: 'always',
    select: (data) =>
      data
        ? {
            session_name: data.session_name ?? null,
            patientName: data.patient?.name ?? null,
            created_time_local: data.created_time_local ?? null,
          }
        : undefined,
  });

  const needsPatientById =
    isAuthenticated &&
    !!chatId &&
    !!patientProfileId &&
    isPatientProfilesFetched &&
    !patientDetailsFromList;

  const { data: patientById } = useQuery({
    queryKey: [GET_PATIENT_PROFILES, { patientProfileId }],
    enabled: needsPatientById,
    queryFn: async () => {
      const { data } = await getPatientProfiles({
        patient_profile_id: patientProfileId!,
      });
      return data.data[0] ?? null;
    },
  });

  const patientDetails = patientDetailsFromList ?? patientById ?? null;

  const resolvedSessionName = useMemo(() => {
    if (!sessionId) return null;
    return (
      sessionDetails?.session_name ||
      sessionDetails?.patient?.name ||
      sessionById?.session_name ||
      sessionById?.patientName ||
      null
    );
  }, [sessionId, sessionDetails, sessionById]);

  const resolvedSessionPatientName = useMemo(() => {
    return sessionDetails?.patient?.name ?? sessionById?.patientName ?? undefined;
  }, [sessionDetails, sessionById]);

  const resolvedSessionCreatedAt = useMemo(() => {
    return sessionDetails?.created_time_local ?? sessionById?.created_time_local ?? undefined;
  }, [sessionDetails, sessionById]);

  const resolvedPatientName = useMemo(() => {
    if (!patientProfileId) return null;
    return buildPatientDisplayName(patientDetails, linkedPatientPlaceholder);
  }, [patientProfileId, patientDetails, linkedPatientPlaceholder]);

  // Wrap setters to track context origin
  const setLinkedSession = useCallback(
    (session: LinkedSessionContext | null) => {
      if (session) {
        setContextOrigin(linkedContextOriginFor(chatId));
      }
      setLinkedSessionAtom(session);
    },
    [chatId, setContextOrigin, setLinkedSessionAtom]
  );

  const setLinkedPatient = useCallback(
    (patient: LinkedPatientContext | null) => {
      if (patient) {
        setContextOrigin(linkedContextOriginFor(chatId));
      }
      setLinkedPatientAtom(patient);
    },
    [chatId, setContextOrigin, setLinkedPatientAtom]
  );

  const clearContext = useCallback(() => {
    clearContextAtom();
  }, [clearContextAtom]);

  // Handle chatId changes - clear context when navigating to a different chat
  useEffect(() => {
    const lastChatId = lastChatIdRef.current;
    const chatIdChanged = lastChatId !== chatId;
    lastChatIdRef.current = chatId;

    // Reset the backend-sync tracker only on a real chatId change so the new
    // chat's session/patient props are treated as a fresh change and sync into
    // the atoms. A bare `contextOrigin` change (e.g. setContextOrigin(chatId))
    // must NOT reset the tracker — that would re-arm the stale-revert branch
    // EVI-2243 closes.
    if (chatIdChanged) {
      lastSyncedBackendRef.current = { sessionId: undefined, patientProfileId: undefined };
    }

    // First render - only clear if context is from a DIFFERENT chat
    if (lastChatId === undefined) {
      // EVI-2500: in-session New chat (page session, no chat yet). PENDING
      // origin from a standalone link is stale here — badges are hidden so
      // the user cannot remove it, and the send path would otherwise post
      // both session_id and patient_profile_id.
      const leakedStandaloneIntoSession =
        !!sessionId &&
        !chatId &&
        (contextOrigin === PENDING_LINKED_CONTEXT_ORIGIN || linkedPatient != null);

      // Context belongs to a specific chat that isn't this one - clear stale data
      // BUT don't clear if:
      // - contextOrigin is null (nothing to clear)
      // - contextOrigin is the pending sentinel (user just linked on new chat)
      // - contextOrigin matches chatId (context is for this chat)
      const isStaleContext =
        leakedStandaloneIntoSession ||
        (contextOrigin !== null &&
          contextOrigin !== PENDING_LINKED_CONTEXT_ORIGIN &&
          contextOrigin !== chatId);

      if (isStaleContext) {
        clearContext();
      }
      return;
    }

    // Chat creation: null → chatId with PENDING context
    // Update origin from PENDING to the actual chatId
    if (!lastChatId && chatId && contextOrigin === PENDING_LINKED_CONTEXT_ORIGIN) {
      setContextOrigin(chatId);
      return;
    }

    // Navigate to new chat: chatId → null
    if (lastChatId && !chatId) {
      clearContext();
      return;
    }

    // Navigate between chats: chatId1 → chatId2
    if (lastChatId && chatId && lastChatId !== chatId) {
      clearContext();
      return;
    }
  }, [chatId, sessionId, linkedPatient, contextOrigin, setContextOrigin, clearContext]);

  // Sync from backend once data loads (via props from useChatBackend).
  //
  // This effect only writes to the atoms when the *backend* props change
  // (initial load, chat data refresh, navigation). It deliberately does NOT
  // re-run when the atoms change — so a user can link a new patient or session
  // mid-chat (after messages exist) without the stale backend
  // `session_id`/`patient_profile_id` reverting their selection. See EVI-2243.
  useEffect(() => {
    if (!chatId) {
      return;
    }

    const backendChanged =
      lastSyncedBackendRef.current.sessionId !== sessionId ||
      lastSyncedBackendRef.current.patientProfileId !== patientProfileId;
    if (!backendChanged) {
      return;
    }
    lastSyncedBackendRef.current = { sessionId, patientProfileId };

    // Sync session from backend
    if (sessionId) {
      const sessionName = resolvedSessionName ?? linkedSessionPlaceholder;
      const patientName = resolvedSessionPatientName;

      if (linkedSession?.id !== sessionId) {
        setLinkedSessionAtom({
          id: sessionId,
          name: sessionName,
          patientName,
          createdAt: resolvedSessionCreatedAt,
        });
        setContextOrigin(chatId);
      }
    } else if (
      linkedSession &&
      contextOrigin !== chatId &&
      contextOrigin !== PENDING_LINKED_CONTEXT_ORIGIN
    ) {
      // Backend has no session, and context doesn't belong to this chat - clear
      setLinkedSessionAtom(null);
    }

    // Sync patient from backend
    if (patientProfileId) {
      const fullName = resolvedPatientName ?? linkedPatientPlaceholder;

      if (linkedPatient?.id !== patientProfileId) {
        setLinkedPatientAtom({
          id: patientProfileId,
          name: fullName,
          demographicString: patientDetails?.demographic_string ?? undefined,
        });
        setContextOrigin(chatId);
      }
    } else if (
      linkedPatient &&
      contextOrigin !== chatId &&
      contextOrigin !== PENDING_LINKED_CONTEXT_ORIGIN
    ) {
      // Backend has no patient, and context doesn't belong to this chat - clear
      setLinkedPatientAtom(null);
    }
  }, [
    chatId,
    sessionId,
    patientProfileId,
    resolvedSessionName,
    resolvedSessionPatientName,
    resolvedSessionCreatedAt,
    resolvedPatientName,
    patientDetails,
    linkedSession,
    linkedPatient,
    contextOrigin,
    linkedSessionPlaceholder,
    linkedPatientPlaceholder,
    setLinkedSessionAtom,
    setLinkedPatientAtom,
    setContextOrigin,
  ]);

  // After reload the backend ids arrive before list/by-id fetches resolve, so
  // the sync effect may write placeholder labels. Enrich once real names load.
  // Only touches atoms whose id matches the backend link (EVI-2243 mid-chat
  // overrides with a different id are left alone).
  useEffect(() => {
    if (!chatId || !sessionId || linkedSession?.id !== sessionId || !resolvedSessionName) {
      return;
    }

    const needsUpdate =
      linkedSession.name !== resolvedSessionName ||
      linkedSession.patientName !== resolvedSessionPatientName ||
      linkedSession.createdAt !== resolvedSessionCreatedAt;

    if (!needsUpdate) return;

    setLinkedSessionAtom({
      id: sessionId,
      name: resolvedSessionName,
      patientName: resolvedSessionPatientName,
      createdAt: resolvedSessionCreatedAt,
    });
  }, [
    chatId,
    sessionId,
    linkedSession,
    linkedSessionPlaceholder,
    resolvedSessionName,
    resolvedSessionPatientName,
    resolvedSessionCreatedAt,
    setLinkedSessionAtom,
  ]);

  useEffect(() => {
    if (
      !chatId ||
      !patientProfileId ||
      linkedPatient?.id !== patientProfileId ||
      !patientDetails ||
      !resolvedPatientName ||
      resolvedPatientName === linkedPatientPlaceholder
    ) {
      return;
    }

    const needsUpdate =
      linkedPatient.name === linkedPatientPlaceholder ||
      linkedPatient.name !== resolvedPatientName ||
      linkedPatient.demographicString !== (patientDetails.demographic_string ?? undefined);

    if (!needsUpdate) return;

    setLinkedPatientAtom({
      id: patientProfileId,
      name: resolvedPatientName,
      demographicString: patientDetails.demographic_string ?? undefined,
    });
  }, [
    chatId,
    patientProfileId,
    linkedPatient,
    linkedPatientPlaceholder,
    patientDetails,
    resolvedPatientName,
    setLinkedPatientAtom,
  ]);

  return {
    isContextLocked,
    linkedSession,
    linkedPatient,
    setLinkedSession,
    setLinkedPatient,
    clearLinkedContext: clearContext,
  };
}
